import React, { type CSSProperties, type ReactNode, useMemo } from 'react';
import { Header } from './Header';
import { DesktopSideNav } from './DesktopSideNav';
import { MobileBottomNav } from './MobileBottomNav';
import { useIsDesktop, useIsMobile } from '../hooks/useMediaQuery';
import { theme } from '@shared/styles/theme';
import { styles } from './Layout.styles';

export interface LayoutProps {
  children: ReactNode;
}

/**
 * Main layout component that wraps all pages.
 * Responsive design with mobile bottom nav and desktop sidebar.
 */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  // Desktop: sidebar nav, full width
  // Tablet: bottom nav, wider container with padding
  // Mobile: bottom nav, max-width 430px centered
  const containerStyle = useMemo((): CSSProperties => {
    if (isDesktop) return styles.containerDesktop;
    if (isMobile) return styles.containerMobile;
    return styles.containerTablet;
  }, [isDesktop, isMobile]);

  const contentStyle = useMemo((): CSSProperties => {
    if (isDesktop) return styles.contentDesktop;
    return styles.contentMobile;
  }, [isDesktop]);

  const innerStyle = useMemo((): CSSProperties => {
    if (isDesktop) return styles.innerDesktop;
    if (isMobile) return styles.innerMobile;
    return styles.innerTablet;
  }, [isDesktop, isMobile]);

  return (
    <div style={containerStyle}>
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '16px';
          e.currentTarget.style.top = '16px';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.overflow = 'visible';
          e.currentTarget.style.zIndex = '99999';
          e.currentTarget.style.padding = '8px 16px';
          e.currentTarget.style.background = theme.colors.background.slate800;
          e.currentTarget.style.color = theme.colors.text.white;
          e.currentTarget.style.borderRadius = '4px';
          e.currentTarget.style.textDecoration = 'none';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.top = 'auto';
          e.currentTarget.style.width = '1px';
          e.currentTarget.style.height = '1px';
          e.currentTarget.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>
      <Header />
      {isDesktop && <DesktopSideNav />}
      <main id="main-content" tabIndex={-1} style={contentStyle}>
        <div style={innerStyle}>{children}</div>
      </main>
      {!isDesktop && <MobileBottomNav />}
    </div>
  );
};
